import React, { useState, useEffect } from 'react';
import { X, Edit, Trash2, FileText, Clock, Mail, Phone, StickyNote, ArrowRightLeft, CalendarDays, User, Tag, Target } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { dealsAPI } from '../lib/api';
import { useCurrency } from '../contexts/CurrencyContext';
import DealCoachingPanel from './DealCoachingPanel';
import { DealComments, AICoachPanel } from './DealCollaboration';

const stageColors = {
  'Prospecting': 'bg-blue-500', 'Qualification': 'bg-yellow-500', 'Proposal': 'bg-purple-500',
  'Negotiation': 'bg-orange-500', 'Closed Won': 'bg-green-500', 'Won': 'bg-green-500',
  'Closed Lost': 'bg-red-500', 'Lost': 'bg-red-500'
};

const priorityColors = {
  'Low': 'text-slate-600', 'Medium': 'text-blue-600', 'High': 'text-amber-600', 'Critical': 'text-red-600'
};

function ActivityIcon({ type }) {
  const iconMap = {
    stage_change: <ArrowRightLeft className="h-4 w-4 text-purple-500" />,
    note: <StickyNote className="h-4 w-4 text-yellow-500" />,
    email: <Mail className="h-4 w-4 text-blue-500" />,
    call: <Phone className="h-4 w-4 text-green-500" />,
    meeting: <CalendarDays className="h-4 w-4 text-indigo-500" />,
  };
  return iconMap[type] || <Clock className="h-4 w-4 text-muted-foreground" />;
}

function ActivityItem({ activity }) {
  const formatTime = (ts) => {
    if (!ts) return '';
    try { return new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return ts; }
  };

  if (activity.type === 'stage_change') {
    return (
      <div className="flex gap-3 items-start" data-testid="activity-stage-change">
        <div className="mt-1"><ActivityIcon type="stage_change" /></div>
        <div className="flex-1 min-w-0">
          <p className="text-sm">
            <span className="font-medium">{activity.changed_by_name}</span> moved deal from{' '}
            <Badge variant="outline" className="text-xs mx-1">{activity.from_stage || 'New'}</Badge> to{' '}
            <Badge variant="outline" className="text-xs mx-1">{activity.to_stage}</Badge>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">{formatTime(activity.timestamp)}</p>
        </div>
      </div>
    );
  }

  if (activity.type === 'note') {
    return (
      <div className="flex gap-3 items-start" data-testid="activity-note">
        <div className="mt-1"><ActivityIcon type="note" /></div>
        <div className="flex-1 min-w-0">
          <p className="text-sm"><span className="font-medium">{activity.created_by_name}</span> added a note</p>
          <p className="text-sm text-muted-foreground mt-1 bg-muted p-2 rounded">{activity.text}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{formatTime(activity.timestamp)}</p>
        </div>
      </div>
    );
  }

  if (activity.type === 'email') {
    return (
      <div className="flex gap-3 items-start" data-testid="activity-email">
        <div className="mt-1"><ActivityIcon type="email" /></div>
        <div className="flex-1 min-w-0">
          <p className="text-sm"><span className="font-medium">{activity.created_by_name}</span> sent an email: <span className="font-medium">{activity.subject}</span></p>
          <p className="text-xs text-muted-foreground mt-0.5">{formatTime(activity.timestamp)}</p>
        </div>
      </div>
    );
  }

  if (activity.type === 'call') {
    return (
      <div className="flex gap-3 items-start" data-testid="activity-call">
        <div className="mt-1"><ActivityIcon type="call" /></div>
        <div className="flex-1 min-w-0">
          <p className="text-sm">
            <span className="font-medium">{activity.created_by_name}</span> logged a {activity.call_type} call
            {activity.duration && <span className="text-muted-foreground"> ({activity.duration})</span>}
          </p>
          {activity.summary && <p className="text-sm text-muted-foreground mt-1 bg-muted p-2 rounded">{activity.summary}</p>}
          <p className="text-xs text-muted-foreground mt-0.5">{formatTime(activity.timestamp)}</p>
        </div>
      </div>
    );
  }

  if (activity.type === 'meeting') {
    return (
      <div className="flex gap-3 items-start" data-testid="activity-meeting">
        <div className="mt-1"><ActivityIcon type="meeting" /></div>
        <div className="flex-1 min-w-0">
          <p className="text-sm"><span className="font-medium">{activity.created_by_name}</span> scheduled a meeting</p>
          {activity.agenda && <p className="text-sm text-muted-foreground mt-1">{activity.agenda}</p>}
          <p className="text-xs text-muted-foreground mt-0.5">{formatTime(activity.timestamp)}</p>
        </div>
      </div>
    );
  }

  return null;
}

export default function ViewDealOffcanvas({ isOpen, onClose, deal, onEdit, onDelete, onConvertToQuote }) {
  const currency = useCurrency();
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => {
    if (isOpen && deal?.id) {
      setActiveTab('details');
      loadActivities();
    }
  }, [isOpen, deal?.id]);

  const loadActivities = async () => {
    if (!deal?.id) return;
    try {
      setLoadingActivities(true);
      const response = await dealsAPI.getActivities(deal.id);
      setActivities(response.data?.activities || []);
    } catch {
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this deal?')) {
      try {
        await dealsAPI.delete(deal.id);
        toast.success('Deal deleted successfully');
        onDelete();
        onClose();
      } catch {
        toast.error('Failed to delete deal');
      }
    }
  };

  const handleConvertToQuote = async () => {
    if (window.confirm('Convert this deal to a quote? This will create a new quote and mark the lead as converted.')) {
      try {
        const response = await dealsAPI.convertToQuote(deal.id);
        toast.success(response.data.message);
        onConvertToQuote(response.data);
        onClose();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to convert deal to quote');
      }
    }
  };

  if (!isOpen || !deal) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full md:w-[620px] bg-card shadow-xl z-50 overflow-y-auto" data-testid="view-deal-offcanvas">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold" data-testid="view-deal-name">{deal.deal_name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={stageColors[deal.stage] || 'bg-gray-500'} data-testid="view-deal-stage">{deal.stage}</Badge>
                {deal.priority && <Badge variant="outline" className={priorityColors[deal.priority]} data-testid="view-deal-priority">{deal.priority}</Badge>}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}><X className="h-5 w-5" /></Button>
          </div>

          {/* Tabs */}
          <div className="flex border-b mb-4">
            <button
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'details' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              onClick={() => setActiveTab('details')}
              data-testid="tab-details"
            >Details</button>
            <button
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'activities' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              onClick={() => setActiveTab('activities')}
              data-testid="tab-activities"
            >Activity Timeline ({activities.length})</button>
            <button
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'coaching' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
              onClick={() => setActiveTab('coaching')}
              data-testid="tab-coaching"
            >AI Coach</button>
          </div>

          {activeTab === 'details' ? (
            <div className="space-y-4">
              {/* Lead & Deal Info */}
              <Card className="p-4">
                <h3 className="font-semibold mb-3 text-sm uppercase text-muted-foreground">Lead & Deal Info</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">Lead:</span><span className="font-medium">{deal.lead_name || '-'}</span></div>
                  {deal.lead_email && <div className="flex justify-between"><span className="text-muted-foreground">Email:</span><span>{deal.lead_email}</span></div>}
                  {deal.lead_phone && <div className="flex justify-between"><span className="text-muted-foreground">Phone:</span><span>{deal.lead_phone}</span></div>}
                  {deal.lead_company && <div className="flex justify-between"><span className="text-muted-foreground">Company:</span><span>{deal.lead_company}</span></div>}
                  {deal.expected_close_date && <div className="flex justify-between"><span className="text-muted-foreground">Expected Close:</span><span>{new Date(deal.expected_close_date).toLocaleDateString()}</span></div>}
                  {deal.source && <div className="flex justify-between"><span className="text-muted-foreground">Source:</span><span>{deal.source}</span></div>}
                  {deal.assigned_to_name && <div className="flex justify-between"><span className="text-muted-foreground">Assigned To:</span><span className="flex items-center gap-1"><User className="h-3 w-3" />{deal.assigned_to_name}</span></div>}
                  <div className="flex justify-between"><span className="text-muted-foreground">Created:</span><span>{new Date(deal.created_at).toLocaleDateString()}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Updated:</span><span>{new Date(deal.updated_at).toLocaleDateString()}</span></div>
                </div>
              </Card>

              {deal.description && (
                <Card className="p-4">
                  <h3 className="font-semibold mb-2 text-sm uppercase text-muted-foreground">Description</h3>
                  <p className="text-sm whitespace-pre-wrap">{deal.description}</p>
                </Card>
              )}

              {/* Line Items */}
              {deal.line_items?.length > 0 && (
                <Card className="p-4">
                  <h3 className="font-semibold mb-3 text-sm uppercase text-muted-foreground">Line Items</h3>
                  <div className="space-y-2">
                    {deal.line_items.map((item, i) => (
                      <div key={i} className="border rounded p-3 text-sm">
                        <div className="flex justify-between mb-1"><span className="font-medium">{item.description}</span><span className="font-semibold">{currency.format((item.quantity * item.price) + ((item.quantity * item.price * item.tax_rate) / 100))}</span></div>
                        <div className="text-muted-foreground text-xs">{item.quantity} x {currency.format(item.price)}{item.tax_rate > 0 && ` + ${item.tax_rate}% tax`}</div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Inventory Items */}
              {deal.items?.length > 0 && (
                <Card className="p-4">
                  <h3 className="font-semibold mb-3 text-sm uppercase text-muted-foreground">Inventory Items</h3>
                  <div className="space-y-2">
                    {deal.items.map((item, i) => (
                      <div key={i} className="border rounded p-3 text-sm">
                        <div className="flex justify-between mb-1"><span className="font-medium">{item.item_name}</span><span className="font-semibold">{currency.format(item.total)}</span></div>
                        <div className="text-muted-foreground text-xs">{item.quantity} x {currency.format(item.unit_price)}{item.tax_rate > 0 && ` + ${item.tax_rate}% tax`}</div>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Totals */}
              <Card className="p-4 bg-primary/5 border-primary/20">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal:</span><span>{currency.format(deal.subtotal || 0)}</span></div>
                  {deal.tax_total > 0 && <div className="flex justify-between text-sm"><span className="text-muted-foreground">Tax:</span><span>{currency.format(deal.tax_total)}</span></div>}
                  {deal.cpq_applied?.length > 0 && (
                    <div className="flex items-center gap-1 text-xs text-emerald-500" data-testid="view-cpq-badge">
                      <span className="font-medium">CPQ Applied:</span>
                      {deal.cpq_applied.map((c, i) => <span key={i} className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-500">{c.rule} ({c.discount}%)</span>)}
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t"><span className="font-semibold">Grand Total:</span><span className="text-2xl font-bold" data-testid="view-deal-total">{currency.format(deal.total)}</span></div>
                  {deal.currency && deal.currency !== 'USD' && (
                    <p className="text-[10px] text-muted-foreground text-right">Currency: {deal.currency}</p>
                  )}
                  {deal.approval_status && (
                    <div className={`text-xs font-medium px-2 py-1 rounded text-center ${
                      deal.approval_status === 'approved' ? 'bg-emerald-500/10 text-emerald-500' :
                      deal.approval_status === 'rejected' ? 'bg-red-500/10 text-red-500' :
                      'bg-amber-500/10 text-amber-500'
                    }`} data-testid="view-approval-status">
                      Approval: {deal.approval_status.replace('_', ' ').toUpperCase()}
                    </div>
                  )}
                </div>
              </Card>

              {deal.notes && (
                <Card className="p-4">
                  <h3 className="font-semibold mb-2 text-sm uppercase text-muted-foreground">Notes</h3>
                  <p className="text-sm whitespace-pre-wrap">{deal.notes}</p>
                </Card>
              )}

              {/* Stage History */}
              {deal.stage_history?.length > 0 && (
                <Card className="p-4">
                  <h3 className="font-semibold mb-3 text-sm uppercase text-muted-foreground">Stage History</h3>
                  <div className="space-y-2">
                    {deal.stage_history.map((entry, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm" data-testid={`stage-history-${i}`}>
                        <ArrowRightLeft className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                        <span className="text-muted-foreground">{entry.from_stage || 'New'}</span>
                        <span>&#8594;</span>
                        <Badge variant="outline" className="text-xs">{entry.to_stage}</Badge>
                        <span className="text-muted-foreground text-xs ml-auto">
                          {entry.changed_by_name && `by ${entry.changed_by_name} `}
                          {entry.changed_at && new Date(entry.changed_at).toLocaleDateString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Collaboration: Comments & AI Coach */}
              <Card className="p-4 space-y-4">
                <DealComments dealId={deal.id} />
                <div className="border-t pt-3">
                  <AICoachPanel dealId={deal.id} dealName={deal.deal_name} />
                </div>
              </Card>

              {/* Actions */}
              <div className="flex gap-3">
                <Button onClick={() => onEdit(deal)} className="flex-1" data-testid="edit-deal-btn"><Edit className="h-4 w-4 mr-2" />Edit Deal</Button>
                <Button onClick={handleConvertToQuote} variant="outline" className="flex-1" data-testid="convert-quote-btn"><FileText className="h-4 w-4 mr-2" />Convert to Quote</Button>
              </div>
              <Button onClick={handleDelete} variant="destructive" className="w-full" data-testid="delete-deal-btn"><Trash2 className="h-4 w-4 mr-2" />Delete Deal</Button>
            </div>
          ) : activeTab === 'coaching' ? (
            /* AI Coaching Tab */
            <DealCoachingPanel dealId={deal.id} dealStage={deal.stage} />
          ) : (
            /* Activities Tab */
            <div className="space-y-4" data-testid="activities-timeline">
              {loadingActivities ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded" />)}
                </div>
              ) : activities.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">No activities yet</p>
                  <p className="text-xs text-muted-foreground mt-1">Notes, emails, calls and stage changes will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {activities.map((activity, i) => (
                    <div key={i} className="border-l-2 border-border pl-4 pb-2">
                      <ActivityItem activity={activity} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
